import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Search, FileText, Folder, FolderPlus, Plus, MoreVertical, Archive, Trash2, X, FolderInput } from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";

export default function DocumentsPage() {
  const { activeWorkspace, canEdit, canAdmin } = useAuth();
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [activeFolder, setActiveFolder] = useState("all"); // all | none | <folder id>
  const [menuOpen, setMenuOpen] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null); // doc being moved

  const load = async () => {
    const [d, f] = await Promise.all([
      api.get(`/workspaces/${activeWorkspace.id}/documents`),
      api.get(`/workspaces/${activeWorkspace.id}/folders`),
    ]);
    setDocs(d.data || []); setFolders(f.data || []);
  };
  useEffect(() => { if (activeWorkspace) load(); /* eslint-disable-next-line */ }, [activeWorkspace]);

  const filtered = useMemo(() => docs.filter((d) => {
    if (activeFolder === "none" && d.folder_id) return false;
    if (activeFolder !== "all" && activeFolder !== "none" && d.folder_id !== activeFolder) return false;
    if (status !== "all" && d.status !== status) return false;
    if (q && !(`${d.title} ${d.document_number} ${d.document_type}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  }), [docs, q, status, activeFolder]);

  const createFolder = async (name, color) => {
    try { await api.post(`/workspaces/${activeWorkspace.id}/folders`, { name, color }); toast.success("Folder created"); setShowNewFolder(false); load(); }
    catch { toast.error("Could not create folder"); }
  };
  const deleteFolder = async (id) => {
    if (!window.confirm("Delete this folder? Documents inside will move to Uncategorised.")) return;
    try { await api.delete(`/workspaces/${activeWorkspace.id}/folders/${id}`); if (activeFolder === id) setActiveFolder("all"); toast.success("Folder deleted"); load(); }
    catch { toast.error("Could not delete folder"); }
  };
  const moveDoc = async (docId, folder_id) => {
    try { await api.patch(`/workspaces/${activeWorkspace.id}/documents/${docId}`, { folder_id: folder_id || null }); toast.success(folder_id ? "Moved to folder" : "Removed from folder"); setMoveTarget(null); load(); }
    catch { toast.error("Could not move"); }
  };
  const archiveDoc = async (docId) => {
    if (!window.confirm("Archive this document?")) return;
    try { await api.delete(`/workspaces/${activeWorkspace.id}/documents/${docId}`); setMenuOpen(null); toast.success("Archived"); load(); }
    catch { toast.error("Could not archive"); }
  };
  const deleteDoc = async (docId) => {
    if (!window.confirm("Permanently delete this document? This cannot be undone.")) return;
    try { await api.delete(`/workspaces/${activeWorkspace.id}/documents/${docId}?hard=true`); setMenuOpen(null); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="max-w-7xl mx-auto p-8" data-testid="documents-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="font-display text-2xl font-bold text-white">Documents</h1><p className="text-sm text-slate-400">Sort documents into folders. Archive or delete when done.</p></div>
        <div className="flex items-center gap-2">
          <button onClick={()=>setShowNewFolder(true)} className="border border-white/10 hover:border-white/20 text-slate-200 text-sm px-3 py-2 rounded-lg inline-flex items-center gap-1" data-testid="new-folder-btn"><FolderPlus size={14}/> New folder</button>
          <Link to="/app/new" className="fx-btn-primary text-white text-sm font-medium px-4 py-2 rounded-lg" data-testid="new-doc-btn">Create document</Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6 min-w-0">
        {/* Folder sidebar */}
        <aside className="fx-panel p-3 space-y-1 h-fit min-w-0 overflow-hidden" data-testid="folder-sidebar">
          <FolderTab active={activeFolder==="all"} onClick={()=>setActiveFolder("all")} icon={FileText} label="All documents" count={docs.length} testid="folder-all"/>
          <FolderTab active={activeFolder==="none"} onClick={()=>setActiveFolder("none")} icon={FileText} label="Uncategorised" count={docs.filter(d=>!d.folder_id).length} testid="folder-none"/>
          <div className="mono text-[10px] uppercase tracking-widest text-slate-500 px-2 pt-3 pb-1">Folders</div>
          {folders.map((f) => (
            <div key={f.id} className="group relative">
              <FolderTab active={activeFolder===f.id} onClick={()=>setActiveFolder(f.id)} icon={Folder} label={f.name} count={f.document_count} color={f.color} testid={`folder-${f.id}`}/>
              <button onClick={()=>deleteFolder(f.id)} className="absolute right-1 top-1.5 text-slate-500 hover:text-red-400 p-1 opacity-0 group-hover:opacity-100" data-testid={`folder-delete-${f.id}`}><Trash2 size={11}/></button>
            </div>
          ))}
          {folders.length === 0 && <div className="text-[11px] text-slate-500 px-2 py-1">No folders yet.</div>}
        </aside>

        <div className="min-w-0">
          <div className="fx-panel p-3 flex flex-wrap items-center gap-2 mb-3 min-w-0">
            <div className="flex items-center gap-2 flex-1 basis-full sm:basis-auto min-w-0 sm:min-w-[220px] bg-[#050816] border border-[#1E293B] rounded-lg px-3 py-1.5">
              <Search size={14} className="text-slate-500"/>
              <input placeholder="Search title, number, type…" value={q} onChange={(e)=>setQ(e.target.value)} className="flex-1 bg-transparent text-sm text-slate-200 outline-none" data-testid="docs-search"/>
            </div>
            <select value={status} onChange={(e)=>setStatus(e.target.value)} className="bg-[#050816] border border-[#1E293B] rounded-lg px-3 py-1.5 text-sm text-slate-200" data-testid="docs-filter">
              <option value="all">All statuses</option><option value="draft">Draft</option><option value="generating">Generating</option><option value="needs_review">Needs review</option><option value="ready">Ready</option><option value="final">Final</option><option value="archived">Archived</option>
            </select>
          </div>

          <div className="fx-panel overflow-visible">
            <div className="overflow-x-auto lg:overflow-visible">
            <table className="w-full text-sm min-w-[520px] lg:min-w-0">
              <thead className="bg-[#0A0F1F] text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-4 py-3">Document</th><th className="text-left px-4 py-3 hidden md:table-cell">Number</th><th className="text-left px-4 py-3 hidden lg:table-cell">Folder</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3 hidden md:table-cell">Updated</th><th className="w-10"></th></tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const folder = folders.find((f)=>f.id === d.folder_id);
                  return (
                    <tr key={d.id} className="border-t border-[#1E293B] hover:bg-white/5 transition">
                      <td className="px-4 py-3">
                        <Link to={`/app/documents/${d.id}`} className="flex items-center gap-2 min-w-0" data-testid={`doc-row-${d.id}`}>
                          <FileText size={14} className="text-cyan-300 shrink-0"/><span className="text-slate-100 truncate">{d.title}</span>
                        </Link>
                      </td>
                      <td className="px-4 py-3 mono text-xs text-slate-400 hidden md:table-cell">{d.document_number}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">{folder ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-[#1E293B]" style={{color: folder.color || '#94a3b8'}}><Folder size={10}/> {folder.name}</span> : <span className="text-xs text-slate-600">—</span>}</td>
                      <td className="px-4 py-3"><StatusBadge status={d.status}/></td>
                      <td className="px-4 py-3 text-slate-500 text-xs hidden md:table-cell">{new Date(d.updated_at).toLocaleString()}</td>
                      <td className="px-2 relative">
                        <button onClick={(e)=>{e.stopPropagation(); setMenuOpen(menuOpen===d.id ? null : d.id);}} className="text-slate-500 hover:text-white p-1" data-testid={`doc-menu-${d.id}`}><MoreVertical size={14}/></button>
                        {menuOpen === d.id && (
                          <div className="absolute right-2 top-full mt-1 fx-panel-2 rounded-lg shadow-xl w-44 z-30">
                            <button onClick={()=>{setMoveTarget(d); setMenuOpen(null);}} className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-white/5 inline-flex items-center gap-2" data-testid={`doc-move-${d.id}`}><FolderInput size={12}/> Move to folder</button>
                            {d.status !== "archived" && <button onClick={()=>archiveDoc(d.id)} className="w-full text-left px-3 py-2 text-xs text-amber-300 hover:bg-white/5 inline-flex items-center gap-2" data-testid={`doc-archive-${d.id}`}><Archive size={12}/> Archive</button>}
                            <button onClick={()=>deleteDoc(d.id)} className="w-full text-left px-3 py-2 text-xs text-red-300 hover:bg-white/5 inline-flex items-center gap-2" data-testid={`doc-delete-${d.id}`}><Trash2 size={12}/> Delete permanently</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-14 text-sm text-slate-500">No documents in this view.<div className="mt-3"><Link to="/app/new" className="fx-btn-primary text-white text-sm font-medium px-4 py-2 rounded-lg">Create document</Link></div></td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>

      {showNewFolder && <NewFolderDialog onClose={()=>setShowNewFolder(false)} onCreate={createFolder}/>}
      {moveTarget && <MoveDialog doc={moveTarget} folders={folders} onClose={()=>setMoveTarget(null)} onMove={(fid)=>moveDoc(moveTarget.id, fid)}/>}
    </div>
  );
}

function FolderTab({ active, onClick, icon: Icon, label, count, color, testid }) {
  return (
    <button onClick={onClick} className={`w-full text-left px-3 py-2 rounded-lg text-sm inline-flex items-center gap-2 transition ${active ? "bg-white/5 text-white" : "text-slate-300 hover:bg-white/5"}`} data-testid={testid}>
      <Icon size={14} style={{color: color || undefined}}/><span className="flex-1 truncate">{label}</span><span className="text-[11px] mono text-slate-500">{count}</span>
    </button>
  );
}

function NewFolderDialog({ onClose, onCreate }) {
  const [name, setName] = useState(""); const [color, setColor] = useState("#20A4FF");
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40" onClick={onClose}>
      <div className="fx-panel p-6 w-full max-w-sm mx-4" onClick={(e)=>e.stopPropagation()} data-testid="new-folder-dialog">
        <div className="flex items-center justify-between mb-4"><div className="font-display text-lg font-semibold">New folder</div><button onClick={onClose} className="text-slate-500 hover:text-white"><X size={16}/></button></div>
        <form onSubmit={(e)=>{e.preventDefault(); if (name.trim()) onCreate(name.trim(), color);}} className="space-y-3">
          <label className="block"><div className="text-xs text-slate-400 mb-1">Folder name</div><input required value={name} onChange={(e)=>setName(e.target.value)} placeholder="e.g. Safety Procedures" className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid="folder-name"/></label>
          <label className="block"><div className="text-xs text-slate-400 mb-1">Colour</div><input type="color" value={color} onChange={(e)=>setColor(e.target.value)} className="w-16 h-10 rounded-md bg-transparent border border-[#1E293B]" data-testid="folder-color"/></label>
          <button className="fx-btn-primary w-full text-white font-medium py-2 rounded-lg inline-flex items-center justify-center gap-2" data-testid="folder-create-submit"><Plus size={14}/> Create folder</button>
        </form>
      </div>
    </div>
  );
}

function MoveDialog({ doc, folders, onClose, onMove }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40" onClick={onClose}>
      <div className="fx-panel p-6 w-full max-w-sm mx-4" onClick={(e)=>e.stopPropagation()} data-testid="move-dialog">
        <div className="flex items-center justify-between mb-4"><div className="font-display text-lg font-semibold">Move document</div><button onClick={onClose} className="text-slate-500 hover:text-white"><X size={16}/></button></div>
        <div className="text-xs text-slate-500 mb-2">Move <span className="text-slate-200">{doc.title}</span> to…</div>
        <div className="space-y-1 max-h-64 overflow-y-auto fx-scrollbar">
          <button onClick={()=>onMove(null)} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-white/5 text-slate-300 inline-flex items-center gap-2" data-testid="move-uncategorised"><FileText size={14}/>Uncategorised</button>
          {folders.map((f) => (
            <button key={f.id} onClick={()=>onMove(f.id)} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-white/5 text-slate-300 inline-flex items-center gap-2" data-testid={`move-${f.id}`}>
              <Folder size={14} style={{color: f.color}}/>{f.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
